import { useWalletsMe } from '@/api/wallets';
import { ErrorTooltipType } from '@/enums/errorTooltipType';
import { RootState } from '@/lib/store';
import { handleChangeAmount } from '@/lib/store/games-preferences/actions';
import { Either, Nullable } from '@starsoft/common/interfaces';
import { Coin } from '@starsoft/common/models';
import { WalletService } from '@starsoft/common/services';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UseBetAmountInputProps, UseBetAmountInputReturn } from './props';
import { AuthService } from '@/services/AuthService';
import { BetStatus } from '@/models/bet/status';
import { getMoneyOrAmount } from '@/utils/account/getMoneyOrAmount';
import { BetStatusId } from '@/models/bet/status/id';

export function useBetAmountInput({
  bet,
  isError,
}: UseBetAmountInputProps): UseBetAmountInputReturn {
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [displayedValue, setDisplayedValue] = useState<string>('');
  const { wallet } = useWalletsMe();
  const dispatch = useDispatch();
  const amount: number = useSelector(
    (state: RootState) => state.gamePreferences.amount,
  );
  const coin: Either<Coin, undefined> = wallet?.currentAccount?.coin;
  const isAuthorized = AuthService.isAuthorized();
  const value: string =
    bet &&
    bet?.status?.id?.toString() == BetStatusId.Open?.toString() &&
    bet?.amount
      ? WalletService.maskCurrency({
          amount: getMoneyOrAmount(bet?.amount),
          coin: bet?.coin,
          hideSymbol: true,
        })
      : displayedValue;

  function getError(): Nullable<ErrorTooltipType> {
    if (!wallet || !!bet) {
      return null;
    }
    const castedAmount = +amount;

    if (coin && castedAmount < getMoneyOrAmount(coin?.minimumBetAmount)) {
      return ErrorTooltipType.UNDERVALUED;
    }

    return null;
  }

  const error: Nullable<ErrorTooltipType> = getError();

  function toggleTouched() {
    setIsTouched(prev => !prev);
  }

  const getMaskedValue = useCallback(
    (value: number): string => {
      return WalletService.maskCurrency({
        amount: value,
        coin,
        hideSymbol: true,
      });
    },
    [coin],
  );

  const getUnmaskedValue = useCallback(
    (value: string): number => {
      return WalletService.unmaskCurrency({
        amount: value,
        coin,
      });
    },
    [coin],
  );

  function onBlur() {
    toggleTouched();
    setIsFocused(false);
    const formattedValue: string = getMaskedValue(amount || 0);
    setDisplayedValue(!amount ? '' : formattedValue);
  }

  function onFocus() {
    setIsFocused(true);
    setIsTouched(false);
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const inputValue: string = e.target.value.replace(/[^0-9,.]/g, '') || '';
    const unmaskedValue: number = WalletService.unmaskCurrency({
      amount: inputValue,
      coin,
    });

    setDisplayedValue(inputValue);
    dispatch(handleChangeAmount(!inputValue ? 0 : unmaskedValue));
  }

  function onMount() {
    if (!amount) {
      return;
    }

    setIsTouched(true);
  }

  useEffect(onMount, [amount]);

  function onFocusChange() {
    if (isFocused || typeof amount === 'undefined') {
      return;
    }

    const formattedValue: string = getMaskedValue(amount ?? 0);
    setDisplayedValue(!amount ? '' : formattedValue);
    dispatch(handleChangeAmount(getUnmaskedValue(formattedValue)));
  }

  useEffect(onFocusChange, [
    amount,
    getMaskedValue,
    isFocused,
    getUnmaskedValue,
    dispatch,
  ]);

  return {
    error,
    value,
    onBlur,
    onFocus,
    onChange,
    isTouched,
    getMaskedValue,
    getUnmaskedValue,
    isError: (error !== null || isError) && isTouched && !!isAuthorized,
  };
}
